import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { PhoneEventsOrg } from '@wetags/phone-events';
import { type CallRecord, NON_PATIENT_CONTACT_TYPES, type UnsavedEditChangesConfig } from '@frontend/api-call-intel';
import {
  CallAnalysis,
  CallAnalyzeStatus,
  CallFlagsData,
  CallInsightBaseProps,
  generateTrackingId,
} from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { IconName } from '@frontend/icons';
import { OnboardingHotspot, useOnboardingShallowStore, HOTSPOT_TARGET } from '@frontend/product-onboarding';
import { theme } from '@frontend/theme';
import { Accordion, Button, Tabs, contextFactory, Text } from '@frontend/design-system';
import { CallBreakdownTabType, CallInsightAccordionType } from '../../types';
import { shouldHideConversationForCallStatus } from '../../utils/call-insight-panel-util';
import { CallActivity, CallDetailsList } from './call-details-section';

interface CallBreakdownAccordionContextValue extends CallInsightBaseProps {
  hydratedCall: HydratedCallRecord;
  callFlags: CallFlagsData;
  hasCallIntelAccess: boolean;
}

const CALL_BREAKDOWN_TAB = {
  ConversationAnalysisTab: 'conversationAnalysis',
  CallDetailsTab: 'callDetails',
  CallActivityTab: 'callActivity',
} as const;

type CallBreakdownTab = (typeof CALL_BREAKDOWN_TAB)[keyof typeof CALL_BREAKDOWN_TAB];

const getAvailableCallBreakdownTabs = ({
  showConversationAnalysisTab,
  showCallActivity,
}: {
  showConversationAnalysisTab: boolean;
  showCallActivity: boolean;
}): CallBreakdownTab[] => {
  const tabs: CallBreakdownTab[] = [CALL_BREAKDOWN_TAB.CallDetailsTab];

  if (showConversationAnalysisTab) {
    tabs.unshift(CALL_BREAKDOWN_TAB.ConversationAnalysisTab);
  }

  if (showCallActivity) {
    tabs.push(CALL_BREAKDOWN_TAB.CallActivityTab);
  }

  return tabs;
};

const getResolvedCallBreakdownTab = ({
  requestedTab,
  showConversationAnalysisTab,
  showCallActivity,
}: {
  requestedTab?: CallBreakdownTabType;
  showConversationAnalysisTab: boolean;
  showCallActivity: boolean;
}): CallBreakdownTab => {
  const availableTabs = getAvailableCallBreakdownTabs({
    showConversationAnalysisTab,
    showCallActivity,
  });

  if (requestedTab && availableTabs.includes(requestedTab)) {
    return requestedTab;
  }

  return availableTabs[0] ?? CALL_BREAKDOWN_TAB.CallDetailsTab;
};

interface CallBreakdownAccordionRootProps extends CallBreakdownAccordionContextValue {
  value?: CallInsightAccordionType | null;
}

interface CallBreakdownAccordionNamespace extends FC<PropsWithChildren<CallBreakdownAccordionRootProps>> {
  Header: typeof CallBreakdownAccordionHeader;
  Body: typeof CallBreakdownAccordionBody;
}

const [CallBreakdownAccordionContext, useCallBreakdownAccordionContext] =
  contextFactory<CallBreakdownAccordionContextValue>(
    'useCallBreakdownAccordionContext must be used within CallBreakdownAccordion'
  );

export const CallBreakdownAccordion: CallBreakdownAccordionNamespace = ({ children, value, ...props }) => {
  return (
    <CallBreakdownAccordionContext.Provider value={props}>
      <Accordion.Item
        value={value || 'callBreakdown'}
        data-testid='call-breakdown-accordion'
        trackingId={generateTrackingId({
          prefix: 'call-insight-panel',
          component: 'call-breakdown-accordion',
          isDemoAccount: props.isDemoAccount,
        })}
      >
        {children}
      </Accordion.Item>
    </CallBreakdownAccordionContext.Provider>
  );
};

const CallBreakdownAccordionHeader: FC<{
  title?: string;
  iconName?: IconName;
  onViewCallPath?: (call: HydratedCallRecord) => void;
}> = ({ title, iconName, onViewCallPath }) => {
  const { t } = useTranslation('call-insight-panel');

  const { hydratedCall } = useCallBreakdownAccordionContext();
  const { completeHotspotByTarget } = useOnboardingShallowStore('completeHotspotByTarget');

  const isOutboundCall = hydratedCall.direction === 'outbound';
  const contactType = hydratedCall.callIntelInsights?.contactType;
  const isNonPatientCall = !!contactType && NON_PATIENT_CONTACT_TYPES.includes(contactType);

  const showViewCallPathButton = !isOutboundCall && !!onViewCallPath;
  const headerTitle = title || t('Call Breakdown');

  return (
    <Accordion.Header
      css={styles.accordionHeader}
      data-testid='call-breakdown-header'
      onClick={() => {
        completeHotspotByTarget(HOTSPOT_TARGET.CALL_INSIGHT_PANEL_CALL_BREAKDOWN, {
          enabled: !isNonPatientCall,
        });
      }}
    >
      <OnboardingHotspot
        target={HOTSPOT_TARGET.CALL_INSIGHT_PANEL_CALL_BREAKDOWN}
        offset={{ top: 2, right: -8 }}
        enabled={!isNonPatientCall}
      >
        {iconName ? (
          <Accordion.Header.Icon icon={iconName} title={headerTitle} />
        ) : (
          <Text weight='bold' css={styles.accordionTitle}>
            {headerTitle}
          </Text>
        )}
      </OnboardingHotspot>

      {showViewCallPathButton && (
        <Button
          iconName='swap-small'
          variant='tertiary'
          size='small'
          data-testid='view-call-path-button'
          trackingId={generateTrackingId({
            prefix: 'call-insight-panel',
            component: 'call-breakdown-header',
            context: 'view-call-path',
          })}
          onClick={() => onViewCallPath(hydratedCall)}
        >
          {t('View Call Path')}
        </Button>
      )}
    </Accordion.Header>
  );
};

interface CallBreakdownAccordionBodyProps {
  defaultSelectedTab?: CallBreakdownTabType;
  hasCallIntelPremiumFeatures: boolean;
  onUpdateCall: (call: CallRecord) => void;
  unsavedEditChangesConfig: UnsavedEditChangesConfig;
  setUnsavedEditChangesConfig: (config: UnsavedEditChangesConfig) => void;
}

const CallBreakdownAccordionBody: FC<CallBreakdownAccordionBodyProps> = ({
  defaultSelectedTab,
  hasCallIntelPremiumFeatures,
  onUpdateCall,
  unsavedEditChangesConfig,
  setUnsavedEditChangesConfig,
}) => {
  const { t } = useTranslation('call-insight-panel');
  const [isAirEventsUiEnabled] = PhoneEventsOrg.useWetag('fe-air-events-ui');
  const {
    call: callInsights,
    callMetadata,
    callFlags,
    hydratedCall,
    isLoading,
    isDemoAccount,
    hasCallIntelAccess,
  } = useCallBreakdownAccordionContext();
  const [isEditMode, setIsEditMode] = useState(false);

  const { isVoicemailCall, isFailedCall } = callFlags;
  const hideConversationAnalysisForStatus = shouldHideConversationForCallStatus({
    status: hydratedCall.status,
    aiOutcome: callInsights?.aiOutcome,
  });
  const showConversationAnalysisTab =
    hasCallIntelAccess &&
    (isDemoAccount || hasCallIntelPremiumFeatures) &&
    !isVoicemailCall &&
    !isFailedCall &&
    !hideConversationAnalysisForStatus;
  // TODO: this needs to be updated - need to check for events
  const hasPhoneTreeEvents = Boolean(hydratedCall.phoneTrees && hydratedCall.phoneTrees.length > 0);
  const hasAiEvents = Boolean(
    hydratedCall.aiMetadata?.aiReceptionistEventTypes && hydratedCall.aiMetadata?.aiReceptionistEventTypes.length > 0
  );
  const showCallActivity = isAirEventsUiEnabled ? hasPhoneTreeEvents || hasAiEvents : hasPhoneTreeEvents;
  const showOnlyCallDetails = !showConversationAnalysisTab && !showCallActivity;
  const [selectedTab, setSelectedTab] = useState<CallBreakdownTab>(() =>
    getResolvedCallBreakdownTab({
      requestedTab: defaultSelectedTab,
      showConversationAnalysisTab,
      showCallActivity,
    })
  );

  const handleTabChange = useCallback((tab: string) => {
    setSelectedTab(tab as CallBreakdownTab);
  }, []);

  useEffect(() => {
    setSelectedTab(
      getResolvedCallBreakdownTab({
        requestedTab: defaultSelectedTab,
        showConversationAnalysisTab,
        showCallActivity,
      })
    );
  }, [defaultSelectedTab, hydratedCall.channelId, showConversationAnalysisTab, showCallActivity]);

  if (showOnlyCallDetails) {
    return (
      <Accordion.Body css={styles.accordionBody} data-testid='call-breakdown-body'>
        <div css={styles.content}>
          <CallDetailsList call={hydratedCall} />
        </div>
      </Accordion.Body>
    );
  }

  return (
    <Accordion.Body css={styles.accordionBody} data-testid='call-breakdown-body'>
      <Tabs controlledTab={selectedTab} onChange={handleTabChange}>
        <Tabs.Bar fullWidth css={{ marginTop: theme.spacing(2) }}>
          {showConversationAnalysisTab && (
            <Tabs.Tab id={CALL_BREAKDOWN_TAB.ConversationAnalysisTab}>{t('Conversation Analysis')}</Tabs.Tab>
          )}
          <Tabs.Tab id={CALL_BREAKDOWN_TAB.CallDetailsTab}>{t('Call Details')}</Tabs.Tab>
          {showCallActivity ? <Tabs.Tab id={CALL_BREAKDOWN_TAB.CallActivityTab}>{t('Call Activity')}</Tabs.Tab> : null}
        </Tabs.Bar>

        <Tabs.Panel controller={CALL_BREAKDOWN_TAB.ConversationAnalysisTab}>
          <div css={styles.content}>
            <CallAnalyzeStatus {...callFlags} type='summary' styles={{ margin: 0 }} />
            <CallAnalysis
              call={callInsights}
              callMetadata={callMetadata}
              callFlags={callFlags}
              isLoading={isLoading}
              isDemoAccount={isDemoAccount}
              isEditMode={isEditMode}
              onUpdateCall={onUpdateCall}
              unsavedEditChangesConfig={unsavedEditChangesConfig}
              setUnsavedEditChangesConfig={setUnsavedEditChangesConfig}
              setIsEditMode={setIsEditMode}
              emptyStateMessages={{
                title: t('No Conversation Analysis available'),
                subTitle: t('Please review the recording.'),
              }}
            />
          </div>
        </Tabs.Panel>

        <Tabs.Panel controller={CALL_BREAKDOWN_TAB.CallDetailsTab}>
          <div css={styles.content}>
            <CallDetailsList call={hydratedCall} />
          </div>
        </Tabs.Panel>

        <Tabs.Panel controller={CALL_BREAKDOWN_TAB.CallActivityTab}>
          <div css={styles.content}>
            <CallActivity call={hydratedCall} />
          </div>
        </Tabs.Panel>
      </Tabs>
    </Accordion.Body>
  );
};

CallBreakdownAccordion.Header = CallBreakdownAccordionHeader;
CallBreakdownAccordion.Body = CallBreakdownAccordionBody;

const styles = {
  accordionHeader: css`
    .header-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: ${theme.spacing(2, 0, 2, 2)};
    }
  `,
  accordionBody: css`
    padding: 0;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    min-height: 100px;
    padding: ${theme.spacing(2)};
    gap: ${theme.spacing(2)};
  `,
  tabContentWithBanners: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  accordionTitle: css`
    font-size: ${theme.fontSize(18)};
  `,
};
